import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "../axios";
import endpoint from "../endpoints";
import { isAuthed } from "../utils/auth";

const WhatsAppAccountContext = createContext(null);

const STORAGE_KEY = "selected_whatsapp_account_id";

export const WhatsAppAccountProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "all";
    } catch {
      return "all";
    }
  });

  const fetchAccounts = useCallback(async () => {
    if (!isAuthed()) return;
    setLoading(true);
    try {
      // Fetch both list to get all connected numbers with status
      const res = await axios.get(endpoint.WHATSAPP_ACCOUNTS);
      const list = res.data?.data?.data || res.data?.data || [];
      setAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load WhatsApp accounts in context:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Listen to custom account-modified events (e.g. added/deleted in /account)
  useEffect(() => {
    const handleAccountUpdated = () => {
      fetchAccounts();
    };
    window.addEventListener("whatsapp_account:updated", handleAccountUpdated);
    window.addEventListener("auth:login", handleAccountUpdated);
    return () => {
      window.removeEventListener("whatsapp_account:updated", handleAccountUpdated);
      window.removeEventListener("auth:login", handleAccountUpdated);
    };
  }, [fetchAccounts]);

  const selectAccount = useCallback((id) => {
    const nextId = id ? String(id) : "all";
    setSelectedAccountId(nextId);
    try {
      localStorage.setItem(STORAGE_KEY, nextId);
    } catch (e) {
      // ignore
    }
    // Dispatch event so any non-react listeners or standalone components can react
    window.dispatchEvent(
      new CustomEvent("whatsapp_account:changed", {
        detail: { accountId: nextId },
      })
    );
  }, []);

  const selectedAccount = useMemo(() => {
    if (!selectedAccountId || selectedAccountId === "all") return null;
    return accounts.find((a) => String(a.id) === String(selectedAccountId)) || null;
  }, [accounts, selectedAccountId]);

  // null = not applicable (no specific account selected, i.e. "all")
  const selectedAccountIsUsable = selectedAccount ? Boolean(selectedAccount.is_usable) : null;

  const value = useMemo(
    () => ({
      accounts,
      selectedAccount,
      selectedAccountId,
      selectedAccountIsUsable,
      selectAccount,
      refreshAccounts: fetchAccounts,
      loading,
      isFiltered: selectedAccountId !== "all" && Boolean(selectedAccount),
    }),
    [accounts, selectedAccount, selectedAccountId, selectedAccountIsUsable, selectAccount, fetchAccounts, loading]
  );

  return (
    <WhatsAppAccountContext.Provider value={value}>
      {children}
    </WhatsAppAccountContext.Provider>
  );
};

export const useWhatsAppAccount = () => {
  const context = useContext(WhatsAppAccountContext);
  if (!context) {
    throw new Error("useWhatsAppAccount must be used within a WhatsAppAccountProvider");
  }
  return context;
};

export default WhatsAppAccountContext;
